import React from 'react';

import type { HubItem } from '../api/types';
import { ExternalLink } from 'lucide-react';
import { NavLink } from 'react-router-dom';

import { PlateWindow } from './Plate';
import { buildArticlePath, buildNewsPath } from '../utils/contentPaths';

const formatDate = (iso: string): string => {
  if (!iso) {
    return '';
  }
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString(undefined, { year: 'numeric', month: 'long' });
};

/**
 * One entry in the article library, arranged as a plate.
 *
 * The sibling of `HubItemRow`, not a replacement for it. The two surfaces are split
 * on lifecycle and their listings should read that way: news is perishable and scans
 * as a dated feed, while an article stays useful for years and is browsed — so the
 * library leads with the plate and gives each piece a whole column.
 *
 * Still not a card. There is no radius, no shadow and no box around the whole entry:
 * the only structure is the hairline above it and the frame around the plate itself,
 * which is what DESIGN.md's No-Card Rule asks for.
 */
const HubItemPlate: React.FC<{ item: HubItem }> = ({ item }) => {
  const to =
    item.kind === 'article'
      ? buildArticlePath(item.slug || item.id)
      : buildNewsPath(item.slug || item.id);

  return (
    <article className="flex flex-col">
      <div className="mb-6 border-t border-rule pt-6" />

      <NavLink to={to} className="group flex flex-1 flex-col">
        <div className="mb-6">
          <PlateWindow
            pictureUrl={item.pictureUrl}
            reservedLabel={item.categoryTitle}
            imageClassName="sepia-[.65] saturate-125 contrast-90 brightness-110 opacity-90 transition-[filter,opacity] duration-fast ease-sheet group-hover:sepia-0 group-hover:saturate-100 group-hover:contrast-100 group-hover:brightness-100 group-hover:opacity-100"
          />
        </div>

        <h3 className="mb-3 line-clamp-2 font-ui text-step-1 text-ink transition-colors duration-fast ease-sheet group-hover:text-apricot">
          {item.title}
          {item.externalUrl && (
            <ExternalLink
              size={14}
              aria-hidden="true"
              className="ml-1.5 inline align-[-2px] text-ink-muted"
            />
          )}
        </h3>

        {item.summary && (
          <p className="mb-4 line-clamp-2 text-step-0 text-ink-muted">{item.summary}</p>
        )}

        {item.date && (
          <div className="mt-auto font-ui text-step--2 font-medium uppercase tracking-[0.18em] text-ink-muted">
            {formatDate(item.date)}
          </div>
        )}
      </NavLink>
    </article>
  );
};

export default HubItemPlate;
